// ---------------------------------------------------------------------------
// Menu management - the shop's side of the digital menu.
//
// Six things: the status, the products as the menu presents them, the
// customer-facing categories, the combos' menu switch, the orders that came
// in, and the settings. Nothing here prices anything.
//
// The products are ONE canonical list in the query cache (`menuKeys.products`).
// Every filter the panel offers - search, category, shown/hidden - is derived
// from that list on the client, synchronously, so a change made in a row is
// reflected in every filtered view the same instant. Inline mutations do not
// invalidate the world: the panel writes the confirmed row back into the
// list itself, and only the caches that actually depend on it are touched.
// When the catalogue outgrows a single list, `fetchMenuProducts` already
// speaks the server's filter language; the panel's selectors would move
// there without a redesign.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type {
  ComboOffer,
  ComboPreview,
  HeroPreview,
  ProductPreview,
  HeroCandidates,
  HeroSlide,
  HeroSlidePayload,
  MenuCategory,
  MenuCategoryPayload,
  MenuOverview,
  MenuProductPayload,
  MenuProductView,
  MenuSettings,
  MenuSettingsPayload,
  MenuStatus,
  OnlineOrder,
  OnlineOrderCounts,
  OnlineOrderStatus,
} from '../types';
import type { PrintPlan } from '../hooks/useSalePrinting';

export const menuKeys = {
  overview: ['menu', 'overview'] as const,
  status: ['menu', 'status'] as const,
  settings: ['menu', 'settings'] as const,
  /// The canonical product list. One key, no filter arguments.
  products: ['menu', 'products'] as const,
  categories: ['menu', 'categories'] as const,
  combos: ['menu', 'combos'] as const,
  hero: ['menu', 'hero'] as const,
  heroCandidates: ['menu', 'hero', 'candidates'] as const,
  orders: (status: string) => ['menu', 'orders', status] as const,
  order: (id: string | null) => ['menu', 'order', id] as const,
};

// --- dashboard ---------------------------------------------------------------

export function useMenuOverview(enabled = true) {
  return useQuery({
    queryKey: menuKeys.overview,
    queryFn: async () => (await api.get<{ overview: MenuOverview }>('/menu/overview')).overview,
    enabled,
    refetchInterval: 30_000,
  });
}

export function useMenuStatus(enabled = true) {
  return useQuery({
    queryKey: menuKeys.status,
    queryFn: async () => (await api.get<{ status: MenuStatus }>('/menu/status')).status,
    enabled,
    refetchInterval: 60_000,
  });
}

// --- settings ----------------------------------------------------------------

export function useMenuSettings(enabled = true) {
  return useQuery({
    queryKey: menuKeys.settings,
    queryFn: async () => api.get<{ settings: MenuSettings; status: MenuStatus }>('/menu/settings'),
    enabled,
  });
}

export function useUpdateMenuSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: MenuSettingsPayload) =>
      api.put<{ settings: MenuSettings; status: MenuStatus; message: string }>('/menu/settings', payload),
    onSuccess: () => {
      // Settings change the status line and the dashboard; nothing else.
      void client.invalidateQueries({ queryKey: menuKeys.settings });
      void client.invalidateQueries({ queryKey: menuKeys.status });
      void client.invalidateQueries({ queryKey: menuKeys.overview });
    },
  });
}

// --- products ----------------------------------------------------------------

/// The server's filter language, for the day the list is too big to hold.
export interface MenuProductFilters {
  search?: string;
  /// A menu category's id, 'none' for the products no category was chosen for.
  menuCategoryId?: string;
  visibility?: 'visible' | 'hidden';
}

export async function fetchMenuProducts(filters: MenuProductFilters = {}): Promise<MenuProductView[]> {
  const query = new URLSearchParams();
  if (filters.search) query.set('search', filters.search);
  if (filters.menuCategoryId) query.set('menuCategoryId', filters.menuCategoryId);
  if (filters.visibility) query.set('visibility', filters.visibility);
  const suffix = query.toString();
  return (await api.get<{ products: MenuProductView[] }>(`/menu/products${suffix ? `?${suffix}` : ''}`)).products;
}

/// The canonical list: every active product with its menu layer and its
/// engine price. Filters are derived from it on the client.
export function useMenuProducts(enabled = true) {
  return useQuery({
    queryKey: menuKeys.products,
    queryFn: () => fetchMenuProducts(),
    enabled,
  });
}

/// One inline change to one product's menu layer. Deliberately invalidates
/// nothing: the panel is the owner of the list and writes the confirmed row
/// back itself - only the NEWEST answer for a row is allowed to land.
export function useUpdateMenuProduct() {
  return useMutation({
    mutationFn: ({ productId, ...payload }: MenuProductPayload & { productId: string }) =>
      api.put<{ product: MenuProductView; message: string }>(`/menu/products/${productId}`, payload),
  });
}


// --- categories --------------------------------------------------------------

export function useMenuCategories(includeInactive = true, enabled = true) {
  return useQuery({
    queryKey: [...menuKeys.categories, includeInactive],
    queryFn: async () =>
      (await api.get<{ categories: MenuCategory[] }>(`/menu/categories${includeInactive ? '?includeInactive=true' : ''}`)).categories,
    enabled,
  });
}

export function useSaveMenuCategory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: MenuCategoryPayload & { id?: string | null }) =>
      id
        ? api.put<{ category: MenuCategory; message: string }>(`/menu/categories/${id}`, payload)
        : api.post<{ category: MenuCategory; message: string }>('/menu/categories', payload),
    onSuccess: () => {
      // A renamed shelf shows its new name in the product rows and its count on the card.
      void client.invalidateQueries({ queryKey: menuKeys.categories });
      void client.invalidateQueries({ queryKey: menuKeys.products });
      void client.invalidateQueries({ queryKey: menuKeys.overview });
    },
  });
}

export function useDeleteMenuParent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/menu/categories/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: menuKeys.categories });
      void client.invalidateQueries({ queryKey: menuKeys.products });
      void client.invalidateQueries({ queryKey: menuKeys.overview });
    },
  });
}

/// Reorder among siblings: the rows whose number changed, saved one by one
/// through the same route a rename uses (names travel with each save so
/// nothing else moves), and the lists refreshed ONCE at the end - not after
/// every row, or the tree would flicker through half-saved orders.
export function useReorderMenuCategories() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (rows: Array<{ id: string; nameAr: string; nameEn: string; sortOrder: number }>) => {
      for (const row of rows) await api.put<{ category: MenuCategory; message: string }>(`/menu/categories/${row.id}`, { nameAr: row.nameAr, nameEn: row.nameEn, sortOrder: row.sortOrder });
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: menuKeys.categories });
      void client.invalidateQueries({ queryKey: menuKeys.products });
      void client.invalidateQueries({ queryKey: menuKeys.overview });
    },
  });
}

// --- the home hero -------------------------------------------------------------

/// The slides with what each points at, read from the canonical rows, and
/// the limits the server enforces.
export function useHeroSlides(enabled = true) {
  return useQuery({
    queryKey: menuKeys.hero,
    queryFn: async () => api.get<{ slides: HeroSlide[]; limits: { brand: number; promo: number } }>('/menu/hero'),
    enabled,
  });
}

/// What may be put on the hero right now: discounted orderable products and active offers.
export function useHeroCandidates(enabled = true) {
  return useQuery({
    queryKey: menuKeys.heroCandidates,
    queryFn: async () => api.get<HeroCandidates>('/menu/hero/candidates'),
    enabled,
    staleTime: 0,
  });
}

export function useSaveHeroSlide() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: HeroSlidePayload & { id?: string | null }) =>
      id ? api.put<{ slide: HeroSlide; message: string }>(`/menu/hero/${id}`, payload) : api.post<{ slide: HeroSlide; message: string }>('/menu/hero', payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: menuKeys.hero });
      void client.invalidateQueries({ queryKey: menuKeys.overview });
    },
  });
}

export function useDeleteHeroSlide() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/menu/hero/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: menuKeys.hero });
      void client.invalidateQueries({ queryKey: menuKeys.overview });
    },
  });
}

// --- live preview: the public DTO for one thing, whatever its state -----------

export function useComboPreview(id: string | null) {
  return useQuery({ queryKey: ['menu', 'preview', 'combo', id], queryFn: () => api.get<ComboPreview>(`/menu/preview/combo/${id}`), enabled: Boolean(id), staleTime: 0 });
}
export function useProductPreview(id: string | null) {
  return useQuery({ queryKey: ['menu', 'preview', 'product', id], queryFn: () => api.get<ProductPreview>(`/menu/preview/product/${id}`), enabled: Boolean(id), staleTime: 0 });
}
export function useHeroPreview(id: string | null) {
  return useQuery({ queryKey: ['menu', 'preview', 'hero', id], queryFn: () => api.get<HeroPreview>(`/menu/preview/hero/${id}`), enabled: Boolean(id), staleTime: 0 });
}

// --- combos ------------------------------------------------------------------

/// The SAME combos Offers & Combos lists, in the same shape - priced by the
/// server and carrying their lifecycle. The menu only reads them.
export function useMenuCombos(enabled = true) {
  return useQuery({
    queryKey: menuKeys.combos,
    queryFn: async () => (await api.get<{ combos: ComboOffer[] }>('/menu/combos?lifecycle=all')).combos,
    enabled,
  });
}

export function useUpdateComboMenu() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; menuVisible?: boolean }) =>
      api.put<{ combo: ComboOffer; message: string }>(`/menu/combos/${id}`, payload),
    onSuccess: ({ combo }) => {
      client.setQueryData<ComboOffer[]>(menuKeys.combos, (list) => (list ? list.map((row) => (row.id === combo.id ? combo : row)) : list));
      void client.invalidateQueries({ queryKey: ['combos'] });
    },
  });
}

// --- online orders -----------------------------------------------------------

export interface OnlineOrderList {
  orders: OnlineOrder[];
  counts: OnlineOrderCounts;
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export function useOnlineOrders(status: OnlineOrderStatus | 'ALL', enabled = true) {
  return useQuery({
    queryKey: menuKeys.orders(status),
    queryFn: async () => api.get<OnlineOrderList>(`/menu/orders?status=${status}&pageSize=100`),
    enabled,
    refetchInterval: status === 'PENDING_CONFIRMATION' ? 15_000 : false,
  });
}

export function useOnlineOrder(id: string | null) {
  return useQuery({
    queryKey: menuKeys.order(id),
    queryFn: async () => (await api.get<{ order: OnlineOrder }>(`/menu/orders/${id}`)).order,
    enabled: Boolean(id),
  });
}

export interface ConfirmOnlineOrderResult {
  order: OnlineOrder;
  alreadyConfirmed: boolean;
  deliveryOrderId: string | null;
  printPlan: PrintPlan | null;
  message: string;
}

const invalidateOrders = (client: ReturnType<typeof useQueryClient>) => {
  void client.invalidateQueries({ queryKey: ['menu', 'orders'] });
  void client.invalidateQueries({ queryKey: ['menu', 'order'] });
  void client.invalidateQueries({ queryKey: menuKeys.overview });
};

export function useConfirmOnlineOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<ConfirmOnlineOrderResult>(`/menu/orders/${id}/confirm`, {}),
    onSuccess: () => {
      invalidateOrders(client);
      // A confirmation is a new delivery order on the till's boards.
      void client.invalidateQueries({ queryKey: ['orders'] });
      void client.invalidateQueries({ queryKey: ['customer-orders'] });
    },
  });
}

export function useRejectOnlineOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string | null }) =>
      api.post<{ order: OnlineOrder; message: string }>(`/menu/orders/${id}/reject`, reason ? { reason } : {}),
    onSuccess: () => invalidateOrders(client),
  });
}
